import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { SignInCard, SignInNotice } from './SignInCard';
import type { SignInFailure } from './signInFailure';
import {
  CompletingStep,
  CredentialsStep,
  ForgotPasswordStep,
  MfaChoiceStep,
  MfaCodeStep,
  MfaSetupStep,
  NewPasswordStep,
  ResetPasswordStep,
} from './SignInSteps';
import {
  SignInMessage,
  SignInOptions,
  SignInView,
  useSignIn,
} from './useSignIn';

export interface SignInScreenProps {
  options: SignInOptions;
  /** Why the user is here, e.g. their session expired; replaced by anything the page itself reports. */
  notice?: SignInNotice;
  /** A refusal the Lambda reported through Cognito (a Google sign-in); explained in place of `notice`. */
  failure?: SignInFailure;
}

const titleFor = (t: TFunction, view: SignInView): string => {
  switch (view.step) {
    case 'new-password':
      return t('Set a new password');
    case 'mfa-code':
    case 'mfa-choice':
      return t('Verify it is you');
    case 'mfa-setup':
      return t('Set up your authenticator app');
    case 'forgot':
    case 'reset':
      return t('Reset your password');
    default:
      return t('Sign in');
  }
};

const noticeFor = (
  t: TFunction,
  { reason, detail }: SignInMessage
): SignInNotice => {
  const failed = (message: string): SignInNotice => ({
    color: 'danger',
    title: t('Sign-in failed'),
    message,
  });
  switch (reason) {
    case 'password_reset_done':
      return {
        color: 'success',
        title: t('Password changed'),
        message: t('Sign in with your new password.'),
      };
    case 'access_denied':
      return {
        color: 'warning',
        title: t('Sign-in cancelled'),
        message: t('Microsoft sign-in was cancelled.'),
      };
    case 'admin_consent_required':
      return {
        color: 'warning',
        title: t('Approval needed'),
        message: t(
          'Your organisation must approve Consolidocs before you can sign in with Microsoft. Ask your IT administrator to grant consent.'
        ),
      };
    case 'password_reset_required':
      return {
        color: 'warning',
        title: t('Password reset needed'),
        message: t(
          'Your password must be reset before you can sign in. Use "Forgot your password?" to choose a new one.'
        ),
      };
    case 'wrong_credentials':
      return failed(t('Incorrect email address or password.'));
    case 'too_many_attempts':
      return failed(
        t('Too many attempts. Wait a few minutes, then try again.')
      );
    case 'invalid_code':
      return failed(t('That code is incorrect or has expired. Try again.'));
    case 'invalid_password':
      return failed(
        [t('That password does not meet the password rules.'), detail]
          .filter(Boolean)
          .join(' ')
      );
    case 'mfa_setup_unavailable':
      return failed(
        t(
          'Your account needs multi-factor authentication set up by an administrator before you can sign in.'
        )
      );
    case 'tenant_not_allowed':
      return failed(
        t(
          'Your organisation is not set up for Microsoft sign-in to Consolidocs.'
        )
      );
    case 'email_not_verified':
      return failed(
        t(
          'Microsoft did not share a verified email address for this account. Sign in with email, or ask your IT administrator.'
        )
      );
    case 'account_not_found':
      return failed(
        t(
          'There is no Consolidocs account for this email address. Ask your administrator to invite you.'
        )
      );
    case 'account_linked':
      return {
        color: 'success',
        title: t('Google account linked'),
        message: t(
          'Your Google login is now linked to your Consolidocs account. Signing you in... If nothing happens, select Sign in with Google again.'
        ),
      };
    case 'account_disabled':
      return failed(
        t('Your Consolidocs account is disabled. Contact your administrator.')
      );
    case 'session_expired':
      return failed(t('The sign-in took too long. Please try again.'));
    case 'not_assigned':
      return failed(
        t(
          'Your organisation has not given you access to Consolidocs yet. Ask your IT administrator to assign you to the Consolidocs app.'
        )
      );
    case 'blocked_by_policy':
      return failed(
        t(
          "Your organisation's sign-in policy blocked this sign-in, for example from this device or network. Contact your IT administrator."
        )
      );
    case 'sign_in_unavailable':
      return failed(
        t(
          'This way of signing in is not set up yet. Try another, or ask your administrator.'
        )
      );
    default:
      return failed(t('We could not sign you in. Please try again.'));
  }
};

/**
 * The app's own sign-in page: Microsoft, Google, and email and password with
 * Cognito's challenges (new password, MFA, MFA setup) and password reset, all
 * on one page. Users are invited, so there is no sign-up.
 */
export const SignInScreen = ({
  options,
  notice,
  failure,
}: SignInScreenProps) => {
  const { t } = useTranslation();
  const signIn = useSignIn(options, failure);
  const message = signIn.message ?? (failure ? { reason: failure } : null);
  const { view, busy } = signIn;
  const stepBusy = busy === 'step';
  const back = signIn.backToSignIn;

  const body = (() => {
    switch (view.step) {
      case 'completing':
        return <CompletingStep />;
      case 'new-password':
        return (
          <NewPasswordStep
            busy={stepBusy}
            onBack={back}
            onSubmit={signIn.submitNewPassword}
          />
        );
      case 'mfa-code':
        return (
          <MfaCodeStep
            busy={stepBusy}
            onBack={back}
            method={view.method}
            destination={view.destination}
            onSubmit={signIn.submitMfaCode}
          />
        );
      case 'mfa-choice':
        return (
          <MfaChoiceStep
            busy={stepBusy}
            onBack={back}
            methods={view.methods}
            onChoose={signIn.chooseMfa}
          />
        );
      case 'mfa-setup':
        return (
          <MfaSetupStep
            busy={stepBusy}
            onBack={back}
            secret={view.secret}
            account={view.account}
            onSubmit={signIn.confirmMfaSetup}
          />
        );
      case 'forgot':
        return (
          <ForgotPasswordStep
            busy={stepBusy}
            onBack={back}
            onSubmit={signIn.sendResetCode}
          />
        );
      case 'reset':
        return (
          <ResetPasswordStep
            busy={stepBusy}
            onBack={back}
            destination={view.destination}
            onSubmit={signIn.resetPassword}
          />
        );
      default:
        return (
          <CredentialsStep
            microsoft={Boolean(options.microsoftClientId)}
            google={Boolean(options.google)}
            busy={busy}
            onMicrosoft={signIn.startMicrosoft}
            onGoogle={signIn.startGoogle}
            onPassword={signIn.signInWithPassword}
            onForgotPassword={signIn.forgotPassword}
          />
        );
    }
  })();

  return (
    <SignInCard
      title={titleFor(t, view)}
      backdrop={options.backdrop}
      notice={message ? noticeFor(t, message) : notice}
    >
      {body}
    </SignInCard>
  );
};
